import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { agreementDocumentsApi } from '../api/agreementDocuments';
import { ApiError } from '../api/client';
import { agreementsKeys } from './useAgreements';
import type {
  AgreementDocument,
  AgreementDocumentCreateInput,
  AgreementDocumentUpdateInput,
  Page,
  UUID,
  WriteResult,
} from '../api/types';

export const adKeys = {
  all: ['agreement-documents'] as const,
  listByAgreement: (agreementId?: UUID) =>
    ['agreement-documents', 'agreement', agreementId] as const,
  detail: (id: UUID) => ['agreement-documents', 'detail', id] as const,
};

// D2-polish 3: agreement_document writes also alter the iaa_initial_date /
// close_date fields surfaced by /agreements (list + detail), so invalidate
// the agreements cache as well.
function invalidateBoth(qc: ReturnType<typeof useQueryClient>): void {
  qc.invalidateQueries({ queryKey: adKeys.all });
  qc.invalidateQueries({ queryKey: agreementsKeys.all, refetchType: 'all' });
}

export function useAgreementDocumentsByAgreement(agreementId?: UUID) {
  return useQuery<Page<AgreementDocument>, ApiError>({
    queryKey: adKeys.listByAgreement(agreementId),
    queryFn: () => agreementDocumentsApi.list({ agreement_id: agreementId }),
    enabled: !!agreementId,
  });
}

export function useAgreementDocument(id: UUID | undefined) {
  return useQuery<AgreementDocument, ApiError>({
    queryKey: adKeys.detail(id ?? ''),
    queryFn: () => agreementDocumentsApi.get(id!),
    enabled: !!id,
  });
}

export function useCreateAgreementDocument() {
  const qc = useQueryClient();
  return useMutation<WriteResult<AgreementDocument>, ApiError, AgreementDocumentCreateInput>({
    mutationFn: agreementDocumentsApi.create,
    onSuccess: (r) => {
      invalidateBoth(qc);
      if (!r.pending) qc.setQueryData(adKeys.detail(r.entity.id), r.entity);
    },
  });
}

export function useUpdateAgreementDocument(id: UUID) {
  const qc = useQueryClient();
  return useMutation<WriteResult<AgreementDocument>, ApiError, AgreementDocumentUpdateInput>({
    mutationFn: (p) => agreementDocumentsApi.update(id, p),
    onSuccess: (r) => {
      invalidateBoth(qc);
      if (!r.pending) qc.setQueryData(adKeys.detail(id), r.entity);
    },
  });
}

export function useDeleteAgreementDocument() {
  const qc = useQueryClient();
  return useMutation<WriteResult<AgreementDocument>, ApiError, UUID>({
    mutationFn: agreementDocumentsApi.delete,
    onSuccess: () => invalidateBoth(qc),
  });
}

export function useRestoreAgreementDocument() {
  const qc = useQueryClient();
  return useMutation<WriteResult<AgreementDocument>, ApiError, UUID>({
    mutationFn: agreementDocumentsApi.restore,
    onSuccess: () => invalidateBoth(qc),
  });
}
